// Landing-URL attribution stash.
//
// Runs once at app boot (main.tsx), BEFORE React renders or any route
// redirects, so tags on the very first URL are never lost. Persisted in
// localStorage for 30 days; a later page view without parameters never
// erases them. The quiz store merges this into the session's attribution
// (captureAttribution for new sessions, ensureSession for existing ones),
// and save-progress merges new keys server-side without overwriting old ones.
//
// Review-site (TPV) A/B tags: lp_variant / lp_vid / lp_dest arrive on links
// from review.bestcareertest.org and are posted back with each purchase
// (netlify/lib/tpvPostback.ts).

export const LANDING_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid',
  // Legacy review-site click id (a-xxxx / b-xxxx) → review webhook click_id.
  'cid',
  // Review-site A/B/C test tags → TPV conversion postback.
  'lp_variant', 'lp_vid', 'lp_dest', 'lp_site',
] as const;

const STORE_KEY = 'jobtest-landing-attr';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface Stash { ts: number; params: Record<string, string> }

function read(): Stash | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stash;
    if (!s || typeof s.ts !== 'number' || Date.now() - s.ts > TTL_MS) return null;
    return s;
  } catch {
    return null;
  }
}

/** Call once at boot. Tags on this URL replace the stash (a new tagged
 *  visit is the freshest attribution); an untagged URL leaves it alone. */
export function stashLandingParams(): void {
  if (typeof window === 'undefined') return;
  try {
    const q = new URLSearchParams(window.location.search);
    const fresh: Record<string, string> = {};
    for (const k of LANDING_KEYS) {
      const v = q.get(k);
      if (v) fresh[k] = v.slice(0, 200);
    }
    const prev = read();
    // First-touch referrer: keep the earliest one we ever saw.
    const initialReferrer = prev?.params.initial_referrer
      ?? (document.referrer && !document.referrer.includes(window.location.host) ? document.referrer.slice(0, 300) : undefined);
    if (Object.keys(fresh).length === 0) {
      if (initialReferrer && !prev) {
        localStorage.setItem(STORE_KEY, JSON.stringify({ ts: Date.now(), params: { initial_referrer: initialReferrer } }));
      }
      return;
    }
    const params: Record<string, string> = { ...fresh };
    if (initialReferrer) params.initial_referrer = initialReferrer;
    localStorage.setItem(STORE_KEY, JSON.stringify({ ts: Date.now(), params }));
  } catch {
    /* storage blocked — the in-URL capture in the quiz store still runs */
  }
}

/** Stashed landing params (≤30 days old), or {}. */
export function getLandingParams(): Record<string, string> {
  return read()?.params ?? {};
}
